'use client';

import React, { useMemo } from 'react';
import type { FC, Key, ReactNode, RefAttributes } from 'react';
import { createContext } from 'react';
import type { SelectProps as AriaSelectProps } from 'react-aria-components';
import {
    Button as AriaButton,
    ListBox as AriaListBox,
    Select as AriaSelect,
    SelectValue as AriaSelectValue,
} from 'react-aria-components';
import { Avatar } from '../avatar';
import { ChipBase } from '../chip/ChipBase';
import { cx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';
import { Popover } from '../popover';
import { SelectField, styles, type SelectSize } from './SelectField';
import useId from '../../../utils/useId';
import { SelectItem, SelectItemBase } from './SelectItem';
import { NativeSelect } from './NativeSelect';
import type { AvatarProps } from '../avatar';

const SEMANTIC_NAME = 'RomaSelect';

export type SelectItemType = {
    id: string;
    label?: string;
    avatar?: AvatarProps;
    isDisabled?: boolean;
    supportingText?: string;
    icon?: FC | ReactNode;
};

export interface CommonProps {
    hint?: string;
    label?: string;
    tooltip?: string;
    size?: SelectSize;
    placeholder?: string | FC | ReactNode;
}

export type SelectProps = Omit<AriaSelectProps<SelectItemType>, 'children' | 'items'> &
    RefAttributes<HTMLDivElement> &
    CommonProps & {
        items?: SelectItemType[];
        popoverClassName?: string;
        popoverContent?: ReactNode;
        leadingIcon?: FC | ReactNode;
        /** Uses the browser-native control rather than Roma's popover list. */
        native?: boolean;
        /** Alias for `selectedKey` (react-aria). Kept for compatibility with native-like Select APIs. */
        value?: Key | null;
        /** Alias for `onSelectionChange` (react-aria). Kept for compatibility with native-like Select APIs. */
        onChange?: (key: Key | null) => void;
        children: ReactNode | ((item: SelectItemType) => ReactNode);
    };

const selectPopoverClassNames: Record<SelectSize, string> = {
    sm: 'flex-none max-h-64! w-(--trigger-width) overflow-x-hidden overflow-y-auto py-1.5',
    md: 'flex-none max-h-80! w-(--trigger-width) overflow-x-hidden overflow-y-auto py-1.5',
    lg: 'flex-none max-h-96! w-(--trigger-width) overflow-x-hidden overflow-y-auto py-1.5',
} as const;

export const SelectContext = createContext<{ size: SelectSize }>({ size: 'sm' });

const Select = (props: SelectProps) => {
    const {
        placeholder = 'Select',
        leadingIcon,
        size = 'sm',
        popoverClassName,
        popoverContent,
        children,
        items,
        label,
        hint,
        tooltip,
        value,
        onChange,
        className,
        selectedKey,
        onSelectionChange,
        native,
        ...rest
    } = props;

    const selectId = useId();
    const labelId = `${selectId}-label`;
    const hintId = `${selectId}-hint`;

    const mergedSelectedKey = selectedKey ?? value;
    const mergedOnSelectionChange = React.useCallback(
        (key: Key | null) => {
            onSelectionChange?.(key);
            onChange?.(key);
        },
        [onSelectionChange, onChange],
    );

    const mergedPopoverClassName = cx(selectPopoverClassNames[size], popoverClassName);

    const placeholderContent = useMemo(() => {
        if (isReactComponent(placeholder)) {
            const Placeholder = placeholder;
            return <Placeholder />;
        }

        return placeholder;
    }, [placeholder]);

    if (native) {
        const { isDisabled, isRequired, isInvalid, name, form, autoFocus, ...nativeProps } = rest;

        return (
            <NativeSelect
                value={mergedSelectedKey == null ? '' : String(mergedSelectedKey)}
                onChange={(event) => mergedOnSelectionChange(event.target.value)}
                disabled={isDisabled}
                required={isRequired}
                aria-invalid={isInvalid}
                aria-label={nativeProps['aria-label']}
                name={name}
                form={form}
                autoFocus={autoFocus}
                label={label}
                hint={hint}
                tooltip={tooltip}
                size={size}
                placeholder={placeholder}
                leadingIcon={leadingIcon}
                items={items}
                className={typeof className === 'string' ? className : undefined}
            />
        );
    }

    return (
        <SelectContext.Provider value={{ size }}>
            <AriaSelect
                {...rest}
                selectedKey={mergedSelectedKey}
                onSelectionChange={mergedOnSelectionChange}
                className={(state) =>
                    cx(
                        SEMANTIC_NAME,
                        'flex flex-col gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            >
                {(state) => (
                    <>
                        <SelectField
                            label={label}
                            hint={hint}
                            tooltip={tooltip}
                            labelFor={selectId}
                            labelId={labelId}
                            hintId={hintId}
                            isRequired={state.isRequired}
                            isInvalid={state.isInvalid}
                            isDisabled={state.isDisabled}
                            size={size}
                            leadingIcon={leadingIcon}
                        >
                            <AriaButton
                                id={selectId}
                                aria-labelledby={labelId}
                                aria-describedby={hint ? hintId : undefined}
                                className={cx(
                                    styles.common.control,
                                    styles.sizes[size].root,
                                    leadingIcon ? styles.sizes[size].leadingPadding : styles.sizes[size].defaultPadding,
                                    styles.sizes[size].trailingPadding,
                                )}
                            >
                                <AriaSelectValue<SelectItemType>
                                    className={cx(
                                        'flex w-full min-w-0 items-center justify-start truncate text-left align-middle',
                                    )}
                                >
                                    {({ selectedItem }) => {
                                        return (
                                            <>
                                                {selectedItem ? (
                                                    selectedItem.avatar ? (
                                                        <ChipBase
                                                            size={size}
                                                            leading={(() => {
                                                                const { className: avatarClassName, ...avatarProps } =
                                                                    selectedItem.avatar;

                                                                return (
                                                                    <Avatar
                                                                        size="xxs"
                                                                        {...avatarProps}
                                                                        className={cx(
                                                                            size === 'sm' ? 'size-3.5' : undefined,
                                                                            avatarClassName,
                                                                        )}
                                                                    />
                                                                );
                                                            })()}
                                                            variant="user"
                                                            isDisabled={state.isDisabled}
                                                            className="-my-0.75"
                                                        >
                                                            <span className="truncate">{selectedItem.label}</span>
                                                        </ChipBase>
                                                    ) : (
                                                        <SelectItemBase
                                                            label={selectedItem.label}
                                                            avatar={selectedItem.avatar}
                                                            supportingText={selectedItem.supportingText}
                                                            icon={selectedItem.icon}
                                                            size={size}
                                                            isDisabled={state.isDisabled}
                                                            className="flex-1 gap-2 p-0"
                                                        />
                                                    )
                                                ) : (
                                                    <SelectItemBase
                                                        size={size}
                                                        isDisabled={state.isDisabled}
                                                        className="flex-1 gap-2 p-0 text-placeholder"
                                                    >
                                                        {placeholderContent}
                                                    </SelectItemBase>
                                                )}
                                            </>
                                        );
                                    }}
                                </AriaSelectValue>
                            </AriaButton>
                        </SelectField>

                        <Popover
                            placement="bottom"
                            offset={4}
                            containerPadding={0}
                            popoverClassName={mergedPopoverClassName}
                            isNonModal={false}
                        >
                            {popoverContent}
                            <AriaListBox items={items} className="size-full outline-hidden">
                                {children}
                            </AriaListBox>
                        </Popover>
                    </>
                )}
            </AriaSelect>
        </SelectContext.Provider>
    );
};

Select.displayName = SEMANTIC_NAME;

const _Select = Select as typeof Select & {
    Item: typeof SelectItem;
};
_Select.Item = SelectItem;

export { _Select as Select };
